import Papa from 'papaparse';
import { StudentData } from './types';
import { devLog, devError, devWarn } from './secureLogger';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface AreaCodeRow {
  'Area Code': string;
  Latitude: string;
  Longitude: string;
}

interface ZipCodeRow {
  ZIP: string;
  'Latitude ': string;
  Longitude: string;
}

interface StudentRow {
  'Unnamed: 0': string;
  'University/College': string;
  Name: string;
  'Academic Class': string;
  email: string;
  'Internal ID': string;
  'University/College Major': string;
  'University/College GPA': string;
  Phone: string;
  SourceFile: string;
  DuplicateCount: string;
  FilesAppearedIn: string;
  Column1: string;
  Column2: string;
  Column3: string;
  Column4: string;
  Column5: string;
  Column6: string;
  Column7: string;
  Column8: string;
  Column9: string;
  Column10: string;
  Column11: string;
  Column12: string;
  Column13: string;
  Column14: string;
  Column15: string;
  Column16: string;
  Column17: string;
  Column18: string;
  'Unnamed: 30': string;
  Resume: string;
}

interface ZipCodeData {
  ZIP: string;
  Latitude: string;
  Longitude: string;
}

interface FilterState {
  majors: string[];
  minGpa: number;
  years: string[];
  skills: string[];
  preferredInstitutions?: string[];
  keywordResults?: string[];
}

let zipCodeMap: Map<string, Coordinates> = new Map();
let areaCodeMap: Map<string, Coordinates> = new Map();

export async function getLocationFromZip(zip: string): Promise<{ latitude: number; longitude: number } | null> {
  try {
    if (zipCodeMap.size === 0) {
      const response = await fetch('/full_us_zip_to_latlon.csv');
      if (!response.ok) {
        devError(`Failed to fetch zip code CSV: ${response.status}`);
        return null;
      }
      
      const csvText = await response.text();
      const result = Papa.parse<ZipCodeRow>(csvText, { header: true });
      
      if (!result.data || result.data.length === 0) {
        devError('No data found in zip code CSV');
        return null;
      }
      
      result.data.forEach((row: ZipCodeRow) => {
        if (row.ZIP && row['Latitude '] && row.Longitude) {
          const latitude = parseFloat(row['Latitude '].trim());
          const longitude = parseFloat(row.Longitude.trim());
          if (!isNaN(latitude) && !isNaN(longitude)) {
            zipCodeMap.set(row.ZIP, {
              latitude,
              longitude
            });
          }
        }
      });
      devLog(`Loaded ${zipCodeMap.size} zip codes into map`);
    }

    const coords = zipCodeMap.get(zip);
    if (!coords) {
      devError(`No coordinates found for zip code: ${zip}`);
      return null;
    }

    devLog(`Found coordinates for zip ${zip}: ${JSON.stringify(coords)}`);
    return coords;
  } catch (error) {
    devError(`Error getting location from zip code: ${error}`);
    return null;
  }
}

// Helper function to extract area code from phone number
const getAreaCodeFromPhone = (phone: string): string | null => {
  if (!phone) {
    devWarn('No phone number provided');
    return null;
  }
  
  // Remove any non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Check if we have enough digits for an area code
  if (digits.length < 3) {
    devWarn(`Phone number too short: ${phone}`);
    return null;
  }
  
  // Extract the area code (first 3 digits)
  const areaCode = digits.substring(0, 3);
  devLog(`Extracted area code ${areaCode} from phone ${phone}`);
  return areaCode;
};

// Helper function to get coordinates from phone number
const getLocationFromPhone = (phone: string | undefined, areaCodeMap: Map<string, Coordinates>): Coordinates => {
  if (!phone) return { latitude: 0, longitude: 0 };
  const areaCode = getAreaCodeFromPhone(phone);
  return areaCode ? (areaCodeMap.get(areaCode) || { latitude: 0, longitude: 0 }) : { latitude: 0, longitude: 0 };
};

// Convert degrees to radians
function toRad(degrees: number): number {
  return degrees * Math.PI / 180;
}

// Calculate distance between two points using Haversine formula
export function calculateDistance(
  lat1: number | undefined,
  lon1: number | undefined,
  lat2: number,
  lon2: number
): number {
  if (lat1 === undefined || lon1 === undefined) {
    return Infinity;
  }
  
  const R = 3958.8; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

export function findStudentsInRadius(
  students: StudentData[],
  centerCoords: { latitude: number; longitude: number },
  radiusMiles: number,
  areaCodeMap: Map<string, Coordinates>
): StudentData[] {
  devLog(`Finding students within ${radiusMiles} miles of ${JSON.stringify(centerCoords)}`);
  devLog(`Total students to check: ${students.length}`);
  
  const filteredStudents = students.filter(student => {
    const areaCode = getAreaCodeFromPhone(student.phone);
    if (!areaCode) {
      devWarn(`Could not extract area code from phone number for student ${student.name}: ${student.phone}`);
      return false;
    }

    const location = areaCodeMap.get(areaCode);
    if (!location) {
      devWarn(`No location found for area code ${areaCode} for student ${student.name}`);
      return false;
    }

    const distance = calculateDistance(
      location.latitude,
      location.longitude,
      centerCoords.latitude,
      centerCoords.longitude
    );
    
    devLog(`Student ${student.name} distance: ${distance.toFixed(2)} miles`);
    return distance <= radiusMiles;
  });

  devLog(`Students within radius: ${filteredStudents.length}`);
  devLog(`Students outside radius: ${students.length - filteredStudents.length}`);
  
  return filteredStudents;
}

// Load CSV data
export const loadData = async (): Promise<StudentData[]> => {
  try {
    devLog('Starting data load...');
    
    // Load area code coordinates
    const areaCodeResponse = await fetch('/us-area-code-geo.csv');
    if (!areaCodeResponse.ok) {
      throw new Error(`Failed to fetch area code CSV: ${areaCodeResponse.status}`);
    }
    
    const areaCodeText = await areaCodeResponse.text();
    const areaCodeResults = Papa.parse<AreaCodeRow>(areaCodeText, { 
      header: true,
      skipEmptyLines: true
    });
    
    if (!areaCodeResults.data || areaCodeResults.data.length === 0) {
      throw new Error('No data found in area code CSV');
    }
    
    areaCodeResults.data.forEach((row: AreaCodeRow) => {
      if (row['Area Code'] && row.Latitude && row.Longitude) {
        const latitude = parseFloat(row.Latitude.trim());
        const longitude = parseFloat(row.Longitude.trim());
        if (!isNaN(latitude) && !isNaN(longitude)) {
          areaCodeMap.set(row['Area Code'], {
            latitude,
            longitude
          });
        }
      }
    });
    devLog(`Loaded ${areaCodeMap.size} area code coordinates`);

    // Load student data
    const studentResponse = await fetch('/students.csv');
    if (!studentResponse.ok) {
      throw new Error(`Failed to fetch student CSV: ${studentResponse.status}`);
    }
    
    const studentText = await studentResponse.text();
    const studentResults = Papa.parse<StudentRow>(studentText, { 
      header: true,
      skipEmptyLines: true
    });
    
    if (!studentResults.data || studentResults.data.length === 0) {
      throw new Error('No data found in student CSV');
    }
    
    const students = studentResults.data.map(processStudentRow);
    devLog(`Loaded ${students.length} students`);
    
    return students;
  } catch (error) {
    devError(`Error loading data: ${error}`);
    throw error;
  }
};

// Filter students by location
export function filterStudentsByLocation(
  students: StudentData[],
  centerZip: string,
  radiusMiles: number,
  areaCodeMap: Map<string, Coordinates>
): Promise<StudentData[]> {
  return new Promise(async (resolve, reject) => {
    try {
      const centerCoords = await getLocationFromZip(centerZip);
      if (!centerCoords) {
        devWarn(`No coordinates found for zip code: ${centerZip}`);
        resolve([]);
        return;
      }

      const filteredStudents = findStudentsInRadius(students, centerCoords, radiusMiles, areaCodeMap);
      resolve(filteredStudents);
    } catch (error) {
      reject(error);
    }
  });
}

// Filter students by other criteria
export const filterStudents = (students: StudentData[], filters: FilterState): StudentData[] => {
  return students.filter(student => {
    // Check if student matches keyword search results
    if (filters.keywordResults && filters.keywordResults.length > 0) {
      if (!filters.keywordResults.includes(student.Internal_ID)) {
        return false;
      }
    }

    // Check preferred institutions
    if (filters.preferredInstitutions && filters.preferredInstitutions.length > 0) {
      if (!filters.preferredInstitutions.includes(student.university)) {
        return false;
      }
    }

    // Check majors
    if (filters.majors.length > 0) {
      if (!filters.majors.includes(student.major)) {
        return false;
      }
    }

    // Check GPA
    if (student.gpa < filters.minGpa) {
      return false;
    }

    // Check years
    if (filters.years.length > 0) {
      if (!filters.years.includes(student.year)) {
        return false;
      }
    }

    // Check skills
    if (filters.skills.length > 0) {
      const studentSkills = Array.isArray(student.skills) 
        ? student.skills.map(skill => skill.toLowerCase())
        : [];
      if (!filters.skills.every(skill => studentSkills.includes(skill.toLowerCase()))) {
        return false;
      }
    }

    return true;
  });
};

// Get unique values for filters
export const getUniqueValues = (students: StudentData[], field: keyof StudentData): string[] => {
  const values = students.map(student => student[field]);
  return Array.from(new Set(values)).filter(Boolean) as string[];
};

// Validate each student record
const isValidStudentRecord = (record: any): record is StudentData => {
  const isValid = record &&
    typeof record['Internal ID'] === 'number' &&
    typeof record['University/College'] === 'string' &&
    typeof record['Name'] === 'string' &&
    typeof record['Academic Class'] === 'string' &&
    typeof record['email'] === 'string' &&
    typeof record['University/College Major'] === 'string' &&
    (typeof record['University/College GPA'] === 'number' || record['University/College GPA'] === null) &&
    typeof record['Phone'] === 'string' &&
    typeof record['Resume'] === 'string' &&
    (typeof record.latitude === 'number' || record.latitude === undefined) &&
    (typeof record.longitude === 'number' || record.longitude === undefined);

  if (!isValid) {
    devLog('Invalid record:', record);
  }
  return isValid;
};

// Resume viewing utility
export const viewResume = (internalId: number) => {
  const pdfPath = `/byid/${internalId}.pdf`;
  window.open(pdfPath, '_blank');
};

function processStudentRow(row: any): StudentData {
  const areaCode = getAreaCodeFromPhone(row.Phone);
  const location = areaCode ? areaCodeMap.get(areaCode) : null;

  return {
    name: row.Name,
    major: row['University/College Major'],
    gpa: parseFloat(row['University/College GPA']) || 0,
    year: row['Academic Class'],
    skills: [],
    phone: row.Phone,
    Internal_ID: row['Internal ID'],
    latitude: location?.latitude,
    longitude: location?.longitude,
    university: row['University/College'],
    email: row.email || ''
  };
}

const processZipCodeRow = (row: ZipCodeRow): { zip: string; coordinates: Coordinates } => {
  return {
    zip: row.ZIP,
    coordinates: {
      latitude: parseFloat(row['Latitude ']),
      longitude: parseFloat(row.Longitude)
    }
  };
};